import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Candy, CupSoda, Wine, Coffee, Popcorn, SprayCan, Lightbulb, Flame, Sparkles, ArrowUpRight,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CATALOG } from "@/data/catalog";
import { Reveal, MaskLines } from "./Reveal";

const ICONS = { Candy, CupSoda, Wine, Coffee, Popcorn, SprayCan, Lightbulb, Flame, Sparkles };

// Asymmetric bento spans
const SPANS = [
  "md:col-span-7", "md:col-span-5",
  "md:col-span-5", "md:col-span-7",
  "md:col-span-4", "md:col-span-4", "md:col-span-4",
  "md:col-span-6", "md:col-span-6",
];

export const Categories = () => {
  const { t, lang } = useLang();
  const c = t.categories;

  return (
    <section id="categories" data-testid="categories-section" className="relative py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div>
            <Reveal>
              <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#D4AF37] mb-6">
                {c.overline}
              </p>
            </Reveal>
            <h2 className="font-display font-medium tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-2xl text-white">
              <MaskLines lines={[c.title]} />
            </h2>
          </div>
          <Reveal delay={0.15}>
            <p className="text-zinc-400 max-w-sm lg:text-right">{c.lead}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5">
          {CATALOG.map((cat, i) => {
            const Icon = ICONS[cat.icon] || Sparkles;
            return (
              <Reveal key={cat.id} delay={(i % 3) * 0.08} className={SPANS[i] || "md:col-span-4"}>
                <Link
                  to={`/catalog#${cat.id}`}
                  data-testid={`category-card-${cat.id}`}
                  className="group relative block h-64 sm:h-72 rounded-2xl overflow-hidden border border-white/10 hover:border-[#D4AF37]/50 transition-colors"
                >
                  <img
                    src={cat.image}
                    alt={cat.name[lang]}
                    className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
                  <div className="relative h-full p-6 sm:p-7 flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <span
                        className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-white/15 bg-black/40 backdrop-blur"
                        style={{ color: cat.accent }}
                      >
                        <Icon size={20} />
                      </span>
                      <ArrowUpRight
                        size={22}
                        className="text-zinc-500 group-hover:text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                      />
                    </div>
                    <div>
                      <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">
                        {cat.name[lang]}
                      </h3>
                      <p className="mt-2 text-sm text-zinc-400 leading-snug line-clamp-2">
                        {cat.tagline[lang]}
                      </p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex justify-center">
            <Link
              to="/catalog"
              data-testid="categories-catalog-cta"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-bold hover:bg-[#F3CE5A] active:scale-95 transition-all"
            >
              {c.cta}
              <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
